import { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Card } from 'react-bootstrap';
import { FaMoneyBillWave, FaInfoCircle, FaPercentage } from 'react-icons/fa';

/**
 * MODAL DE CONFIRMATION AVEC IMPÔT - Modal pour Dividend/Capital avec champ impôt optionnel
 * @param {Object} props
 * @param {boolean} props.show - Si le modal est visible
 * @param {Function} props.onHide - Fonction pour fermer le modal
 * @param {Function} props.onConfirm - Fonction appelée avec le montant d'impôt
 * @param {Object} props.event - L'événement à distribuer
 * @param {boolean} props.loading - État de chargement
 * @param {boolean} props.withTaxField - Si le champ impôt doit être affiché
 */
const CashDistributionModal = ({ show, onHide, onConfirm, event, loading, withTaxField = false }) => {
  const [impot, setImpot] = useState('');
  const [error, setError] = useState('');

  /**
   * Réinitialise le formulaire quand le modal s'ouvre
   */
  useEffect(() => {
    if (show) {
      setImpot('');
      setError('');
    }
  }, [show]);

  /**
   * Valide le champ impôt
   */
  const validateImpot = () => {
    if (!withTaxField || !impot) {
      return true; // Champ optionnel, validation ok si vide
    }

    const impotValue = parseFloat(impot);
    
    if (isNaN(impotValue)) {
      setError('Veuillez saisir un montant valide');
      return false;
    }

    if (impotValue < 0) {
      setError('Le montant d\'impôt ne peut pas être négatif');
      return false;
    }

    // Vérification selon le type d'événement
    if (event.type === 'Dividend to be paid') {
      if (impotValue > event.amount) {
        setError('L\'impôt ne peut pas dépasser le montant du dividende');
        return false;
      }
    } else if (event.type === 'Capital repayment') {
      if (impotValue > event.capital) {
        setError('L\'impôt ne peut pas dépasser le montant du capital');
        return false;
      }
    }

    setError('');
    return true;
  };

  /**
   * Gère la soumission du formulaire
   */
  const handleSubmit = () => {
    if (!validateImpot()) {
      return;
    }

    // Convertit l'impôt en number ou null si vide
    const impotValue = impot ? parseFloat(impot) : null;
    onConfirm(impotValue);
  };

  if (!event) return null;

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      centered
      size="lg"
    >
      <Modal.Header closeButton className="border-bottom-0">
        <div className="d-flex align-items-center">
          <div className="bg-success bg-opacity-10 p-2 rounded-circle me-3">
            <FaMoneyBillWave className="text-success" size={24} />
          </div>
          <div>
            <Modal.Title className="h5 mb-1">
              Confirmer la Distribution de Cash
            </Modal.Title>
            <div className="text-muted small">
              {event.type} - {withTaxField ? 'Avec option d\'impôt' : 'Sans impôt'}
            </div>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="py-4">
        {/* Alerte d'information */}
        <Alert variant={withTaxField ? "primary" : "success"} className="border-0 bg-light mb-4">
          <div className="d-flex align-items-start">
            <FaInfoCircle className={`mt-1 me-3 flex-shrink-0 text-${withTaxField ? 'primary' : 'success'}`} />
            <div>
              <strong>Distribution de {event.type}</strong>
              <p className="mb-0 small">
                {withTaxField 
                  ? 'Vous pouvez optionnellement saisir un montant d\'impôt à prélever.'
                  : 'Aucun impôt ne sera prélevé lors de cette distribution.'
                }
              </p>
            </div>
          </div>
        </Alert>

        {/* Détails de l'événement */}
        <Card className="border-0 bg-light mb-4">
          <Card.Body>
            <h6 className="card-title text-dark mb-3">Détails de l'OST</h6>
            <div className="row g-3">
              <DetailItem label="Type" value={event.type} />
              <DetailItem label="Titre" value={event.securityName} />
              <DetailItem label="ISIN" value={event.securityIsin} />
              <DetailItem 
                label={event.type === 'Dividend to be paid' ? 'Montant Dividende' : 'Montant Capital'} 
                value={`${(event.amount || event.capital || 0).toLocaleString('fr-FR')}`}
                highlight 
              />
              <DetailItem label="Marché" value={event.targetMarket} />
              <DetailItem 
                label="Échéance" 
                value={event.dateDePlanification ? 
                  new Date(event.dateDePlanification).toLocaleDateString('fr-FR') : 
                  'Non définie'
                }
              />
            </div>
          </Card.Body>
        </Card>

        {/* Champ impôt optionnel (seulement si withTaxField = true) */}
        {withTaxField && (
          <div className="mb-4">
            <Form.Group>
              <Form.Label className="fw-semibold d-flex align-items-center">
                <FaPercentage className="me-2 text-muted" />
                Montant d'impôt (optionnel)
                <span className="text-muted ms-1 small"> - Laisser vide pour aucun impôt</span>
              </Form.Label>
              <div className="input-group">
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0"
                  max={event.type === 'Dividend to be paid' ? event.amount : event.capital}
                  value={impot}
                  onChange={(e) => {
                    setImpot(e.target.value);
                    if (error) setError('');
                  }}
                  isInvalid={!!error}
                  placeholder="Saisir le montant d'impôt..."
                  className="border-end-0"
                />
                {/* <span className="input-group-text bg-white border-start-0">Fcfa</span> */}
              </div>
              {error && <Form.Text className="text-danger small mt-2">{error}</Form.Text>}
              <Form.Text className="text-muted small mt-2">
                Capital ou Dividende OST: {
                  (event.type === 'Dividend to be paid' ? event.amount : event.capital || 0)
                    .toLocaleString('fr-FR')
                }
              </Form.Text>
            </Form.Group>
          </div>
        )}

        {/* Avertissement */}
        <Alert variant="warning" className="border-0 small">
          <FaInfoCircle className="me-2" />
          <strong>Attention:</strong> Cette action est irréversible. Une fois distribuée, 
          la corporate action sera marquée comme traitée.
        </Alert>
      </Modal.Body>

      <Modal.Footer className="border-top-0">
        <Button 
          variant="outline-secondary" 
          onClick={onHide}
          disabled={loading}
        >
          Annuler
        </Button>
        <Button 
          variant={withTaxField ? "primary" : "success"} 
          onClick={handleSubmit}
          disabled={loading}
          className="d-flex align-items-center gap-2"
        >
          {loading ? (
            <>
              <div className="spinner-border spinner-border-sm" role="status" />
              Distribution en cours...
            </>
          ) : (
            <>
              <FaMoneyBillWave />
              {withTaxField ? 'Distribuer avec ces paramètres' : 'Distribuer sans impôt'}
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// Composant helper pour afficher les détails
const DetailItem = ({ label, value, highlight = false }) => (
  <div className="col-6 col-md-4">
    <div className="small text-muted mb-1">{label}</div>
    <div className={`fw-semibold ${highlight ? 'text-success' : 'text-dark'}`}>
      {value}
    </div>
  </div>
);

export default CashDistributionModal;